import React, {useContext, useEffect, useState} from 'react';
import AccessControl from "../common/AccessControl";
import {accessibilityRoles} from "../../utils/accessUtils";
import {Button, Modal, Space, Spin, Upload, Image} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {
    DeleteOutlined,
    DownloadOutlined,
    InboxOutlined,
    PlusOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    FileOutlined
} from "@ant-design/icons";
import {DocumentResponse} from "../../api-client";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {AlertContext} from "../../context/AlertContext";
import {getDocumentApi, getDocumentsApi} from "../../api/apiInstances";
import logger from "../../utils/logger";
import {IMAGE_EXTENSIONS, validateImageFile, validateDocumentFile} from "../../utils/fileValidation";
import {ALERT_DURATIONS} from "../../constants/alerts";

// Document type constants (matching Java backend)
const AMBULATORY_LIST_DOCUMENT_TYPE = 1; // Dental ambulatory visit record (DB_amb.pdf)

// File type detection helpers
const isImageFile = (filename: string): boolean => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return IMAGE_EXTENSIONS.includes(ext);
};

const getFileIcon = (filename: string) => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));

    switch (ext) {
        case '.pdf':
            return <FilePdfOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />;
        case '.doc':
        case '.docx':
            return <FileWordOutlined style={{ fontSize: '48px', color: '#2b579a' }} />;
        case '.xls':
        case '.xlsx':
            return <FileExcelOutlined style={{ fontSize: '48px', color: '#217346' }} />;
        case '.txt':
        case '.csv':
            return <FileTextOutlined style={{ fontSize: '48px', color: '#666' }} />;
        default:
            return <FileOutlined style={{ fontSize: '48px', color: '#999' }} />;
    }
};

const Documents = () => {

    const intl = useIntl();

    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedDocument, setSelectedDocument] = useState<string>('');
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const {setVisible, setSuccessMessage, setError} = useContext(AlertContext);
    const {userOrganization} = useContext(DentobookApplicationContext);

    const {Dragger} = Upload;

    const [documents, setDocuments] = useState<DocumentResponse[]>([]);
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const documentApi = getDocumentApi();
    const documentOrganizationApi = getDocumentsApi();

    const generatePdf = () => {
        // CRITICAL: Must specify responseType: 'blob' to handle binary PDF data correctly
        documentApi.generatePdfForPrint(AMBULATORY_LIST_DOCUMENT_TYPE, { responseType: 'blob' })
            .then((pdfResponse) => {
                // Create blob URL and trigger download with proper filename
                const blob = new Blob([pdfResponse.data], {type: 'application/pdf'});
                const url = window.URL.createObjectURL(blob);

                // Create temporary link element to trigger download
                const link = window.document.createElement('a');
                link.href = url;
                link.download = 'Dental Visit Record.pdf'; // Filename for download
                link.click();

                // Clean up the blob URL after a delay
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
            })
            .catch();
    }

    const loadImagePreview = async (document: DocumentResponse) => {
        // Only load preview for image files
        if (!isImageFile(document.fileName || '')) {
            return;
        }

        try {
            // Get presigned URL from backend (efficient - no file download needed)
            const response = await (documentOrganizationApi as any).getClinicDocumentPreviewUrl(document.id!);
            const presignedUrl = response.data.url;

            setImageUrls(prev => ({
                ...prev,
                [document.id!]: presignedUrl
            }));
        } catch (err) {
            logger.log('Failed to load image preview', err);
        }
    };

    const openModal = () => setModalOpen(true);

    const handleCancel = () => {
        setModalOpen(false);
    };

    const handleDelete = () => {
        documentOrganizationApi.deleteOrganizationDocumentById(selectedDocument)
            .then(() => {
                setSuccessMessage(intl.formatMessage({id: 'delete_document'}));
                setVisible(true);
                setModalOpen(false);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                setDocuments(documents.filter(documentItem => documentItem.id !== selectedDocument));
            })
            .catch(err => {
                logger.log(err);
            });
    }

    useEffect(() => {
        documentOrganizationApi.fetchClinicDocuments(userOrganization?.id!)
            .then(res => {
                setDocuments(res.data);
                // Load image previews for all image files
                res.data.forEach((doc: DocumentResponse) => {
                    if (isImageFile(doc.fileName || '')) {
                        loadImagePreview(doc);
                    }
                });
            })
            .catch(err => logger.log(err))
    }, [userOrganization]);

    // Upload file when pendingFile changes (triggered by onChange)
    useEffect(() => {
        if (!pendingFile) return;

        const uploadFile = async () => {
            setIsUploading(true);

            try {
                // @ts-ignore
                const res = await documentOrganizationApi.createDocument_1(pendingFile);
                const newDoc = res.data!;
                setDocuments(prevState => prevState.concat(newDoc));

                if (isImageFile(newDoc.fileName || '')) {
                    loadImagePreview(newDoc);
                }

                setSuccessMessage(intl.formatMessage({id: 'success'}));
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            } catch (err: any) {
                logger.log(err);
                const errorMessage = err?.response?.data?.detail || intl.formatMessage({id: 'invalid_image_format'});
                setError(errorMessage);
                setVisible(true);
                setTimeout(() => {
                    setError(false);
                    setVisible(false);
                }, ALERT_DURATIONS.STANDARD);
            } finally {
                setIsUploading(false);
                setPendingFile(null); // Clear pending file
            }
        };

        uploadFile();
    }, [pendingFile]);
    return (

        <AccessControl
            accessibleFor={[accessibilityRoles.CLINIC_ADMIN, accessibilityRoles.MEDICAL_DOCTOR, accessibilityRoles.MEDICAL_ASSISTANT, accessibilityRoles.RECEPTIONIST, accessibilityRoles.DENTAL_TECHNICIAN]}>
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn",
                    danger: true
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                className="dentobook-modal" title={<FormattedMessage id="confirmation_text"/>}
                okText={intl.formatMessage({id: 'delete_action'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={isModalOpen} onOk={handleDelete}
                onCancel={handleCancel}
            >
                <p><FormattedMessage id="document_delete_confirm"/></p>
            </Modal>

            {/* Organization Documents Section */}
            <div style={{width: '100%', paddingTop: '25px'}}>
                <h2 style={{marginBottom: '20px', fontSize: '18px', fontWeight: '500'}}>
                    <FormattedMessage id="organization_documents" defaultMessage="Organization Documents"/>
                </h2>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '24px',
                    width: '100%'
                }}>
                    {documents.map(document => (
                    <div className="document-item" key={document.id} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        padding: '16px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff'
                    }}>
                        {/* Preview/Icon Section - Centered */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '150px',
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                            padding: '16px'
                        }}>
                            {isImageFile(document.fileName || '') ? (
                                // Show image preview for image files
                                imageUrls[document.id!] ? (
                                    <Image
                                        src={imageUrls[document.id!]}
                                        alt={document.fileName}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '200px',
                                            objectFit: 'contain',
                                            borderRadius: '4px'
                                        }}
                                        preview={{
                                            src: imageUrls[document.id!]
                                        }}
                                    />
                                ) : (
                                    <Spin size="default" />
                                )
                            ) : (
                                // Show file type icon for documents
                                getFileIcon(document.fileName || '')
                            )}
                        </div>

                        {/* Filename */}
                        <div className="document-title" style={{
                            textAlign: 'center',
                            padding: '8px 0',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <span className="body-large-medium" style={{
                                wordBreak: 'break-word',
                                display: 'block'
                            }}>{document.fileName}</span>
                        </div>

                        {/* Action Buttons */}
                        <Space style={{justifyContent: 'center', width: '100%'}}>
                            <Button
                                style={{width: '123px', height: '32px'}}
                                className="dentobook-btn-secondary modal-btn"
                                icon={<DownloadOutlined/>}
                                onClick={() => {
                                    documentOrganizationApi.fetchClinicDocument(document.id!, { responseType: 'blob' })
                                        .then((pdfResponse) => {
                                            // Create blob URL from response data
                                            const blob = new Blob([pdfResponse.data], {type: 'application/octet-stream'});
                                            const url = window.URL.createObjectURL(blob);
                                            const link = window.document.createElement('a');
                                            link.href = url;
                                            link.download = document.fileName || 'download';
                                            link.click();
                                            // Clean up
                                            setTimeout(() => window.URL.revokeObjectURL(url), 100);
                                        })
                                        .catch();
                                }}
                            >
                                <FormattedMessage id="download" defaultMessage="Download"/>
                            </Button>
                            <Button
                                style={{width: '123px', height: '32px', color: '#FF4D4F'}}
                                className="dentobook-btn-secondary modal-btn"
                                icon={<DeleteOutlined/>}
                                onClick={() => {
                                    setSelectedDocument(document.id!);
                                    openModal();
                                }}
                            >
                                <FormattedMessage id="delete_action" defaultMessage="Delete"/>
                            </Button>
                        </Space>
                    </div>
                ))}
                </div>
            </div>

            {/* Platform Forms Section — Bulgaria-specific documents */}
            {userOrganization?.country === 'BG' && <div style={{width: '100%', marginTop: '40px'}}>
                <h2 style={{marginBottom: '20px', fontSize: '18px', fontWeight: '500'}}>
                    <FormattedMessage id="platform_forms" defaultMessage="Platform Forms"/>
                </h2>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '24px',
                    width: '100%'
                }}>
                    {/* Ambulatory List - Static PDF Template */}
                    <div className="document-item" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        padding: '16px',
                        border: '2px solid #1890ff',
                        borderRadius: '8px',
                        backgroundColor: '#f0f8ff'
                    }}>
                        {/* PDF Icon */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '150px',
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            padding: '16px'
                        }}>
                            <FilePdfOutlined style={{ fontSize: '64px', color: '#1890ff' }} />
                        </div>

                        {/* Title */}
                        <div className="document-title" style={{
                            textAlign: 'center',
                            padding: '8px 0',
                            borderBottom: '1px solid #d9d9d9'
                        }}>
                            <span className="body-large-medium" style={{
                                display: 'block'
                            }}>
                                <FormattedMessage id="ambulatory_list"/>
                            </span>
                        </div>

                        {/* Download Button Only - No Delete */}
                        <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                            <Button
                                style={{width: '123px', height: '32px'}}
                                className="dentobook-btn-secondary modal-btn"
                                icon={<DownloadOutlined/>}
                                onClick={() => {
                                    generatePdf()
                                }}
                            >
                                <FormattedMessage id="download" defaultMessage="Download"/>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>}

            {/* Upload Section - Full Width Below Grid */}
            <div style={{width: '100%', marginTop: '24px'}}>
                <Spin spinning={isUploading} tip={intl.formatMessage({id: 'loading'})}>
                <Dragger name="file"
                         showUploadList={false}
                         accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.bmp"
                         customRequest={() => {}}
                         onChange={(info) => {
                             // Synchronous onChange (like avatar uploads) - just store the file
                             const actualFile = info.file.originFileObj;
                             if (!actualFile) return;

                             // Validate before storing
                             const documentValidation = validateDocumentFile(actualFile);
                             const imageValidation = validateImageFile(actualFile);

                             if (!documentValidation.isValid && !imageValidation.isValid) {
                                 const errorKey = documentValidation.errorKey || 'invalid_image_format';
                                 setError(intl.formatMessage({id: errorKey}));
                                 setVisible(true);
                                 setTimeout(() => {
                                     setError(false);
                                     setVisible(false);
                                 }, ALERT_DURATIONS.STANDARD);
                                 return;
                             }

                             // Store file - upload will happen in useEffect
                             setPendingFile(actualFile);
                         }}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined/>
                    </p>
                    <p className="ant-upload-text"><FormattedMessage id={"choose_file_dropbox"}/></p>
                    <br/>
                    <Button shape={"round"} color={"blue"} size={"large"}>
                        <PlusOutlined/>
                        {' '}
                        <FormattedMessage id={"choose"}/>
                    </Button>
                </Dragger>
                </Spin>
            </div>
        </AccessControl>
    );
};

export default Documents;